import {
	Motivations,
	PERSONA_AGE_GROUPS,
	SelectableItem,
	TargetAudience,
	UserPersona,
} from '../../../commonTypes';
import { normalizePersonaGender } from '../../../ai/personaImagePrompt';

const extractFromSingleLine = (text: string, key: string): string => {
	const match = text.match(new RegExp(`${key}:\\s*\\**(.+)\\**`, 'i'));
	return match ? match[1].trim() : '';
};

const extractFromNextLines = (text: string, key: string): string => {
	const match = text.match(new RegExp(`${key}:\\s*\n?((.|\n)+?)\n\n?`, 'mi'));
	if (match) {
		return match[1].trim();
	}
	const match2 = text.match(new RegExp(`${key}:\\s*\n?((.|\n)+?)$`, 'mi'));
	if (match2) {
		return match2[1].trim();
	}
	return '';
};

const uppercaseFirstLetter = (text: string): string => {
	return text.charAt(0).toUpperCase() + text.slice(1);
};

const ENCODED_THEME_MARKERS =
	/project[\s/]+framing|pdf[\s/]+overview|not[\s/]+provided|psychographic[\s/]+analysis/i;

/** Prompt dumps stored as slash-joined themes, e.g. project/framing/not/provided/pdf/overview/... */
export const isEncodedPromptTheme = (theme?: string | null): boolean => {
	const value = (theme || '').trim();
	if (!value) return false;
	if (ENCODED_THEME_MARKERS.test(value)) return true;
	return (value.match(/\//g) || []).length >= 2;
};

export const displayablePersonaTheme = (theme?: string | null): string => {
	const value = (theme || '').trim();
	if (!value || isEncodedPromptTheme(value)) return '';
	return value;
};

export const personaThemeFromSearchTerm = (searchTerm: string): string => {
	const compact = searchTerm.replace(/\s+/g, ' ').trim();
	if (!compact) return '';
	if (isEncodedPromptTheme(compact) || compact.length > 80) {
		const perspective = compact.match(/creative perspective:\s*([^.\n]+)/i);
		const short = perspective?.[1]?.trim();
		if (short && short.length <= 80 && !isEncodedPromptTheme(short)) return short;
		return '';
	}
	return compact;
};

export const parseUserPersona = (themeString: string, personaText: string): UserPersona => {
	const theme = uppercaseFirstLetter(displayablePersonaTheme(themeString));

	const parsedGender = normalizePersonaGender(extractFromSingleLine(personaText, 'Gender'));

	return {
		theme,
		ageGroup: PERSONA_AGE_GROUPS.find(
			(group) => group === extractFromSingleLine(personaText, 'Age Group')
		) || '25–34',
		name:
			extractFromSingleLine(personaText, 'Name') ||
			extractFromSingleLine(personaText, 'User Persona'),
		...(parsedGender ? { gender: parsedGender } : {}),
		biography: extractFromNextLines(personaText, 'Biography'),
		goals: extractFromNextLines(personaText, 'Goals'),
		scores: {
			tech: parseInt(extractFromSingleLine(personaText, 'Technical Knowledge'), 10),
			ambition: parseInt(extractFromSingleLine(personaText, 'Ambition'), 10),
			happiness: parseInt(extractFromSingleLine(personaText, 'Happiness'), 10),
		},
		influences:
			extractFromNextLines(personaText, 'Influences') ||
			extractFromNextLines(personaText, 'Influencers') ||
			extractFromNextLines(personaText, 'Influencer') ||
			extractFromNextLines(personaText, 'Who Influences Them Most'),
		motivations: extractFromNextLines(personaText, 'Motivations'),
		needs: extractFromNextLines(personaText, 'Needs and Wants'),
		painPoints: extractFromNextLines(personaText, 'Pain Points'),
	};
};

export const parseMotivations = (motivationsString: string, title: string): Motivations => {
	const sentences = motivationsString.split('\n\n');
	const intro = sentences[0];
	const outro = sentences.at(-1) || '';
	const stakeholders = sentences.slice(1, -1).map((sentence) => {
		const match = sentence.match(new RegExp(`(?:[0-9]. ?\\**)?([\\w- ]+)\\**:\\s?\n?(.+)`, 'i'));
		const name = match ? match[1].trim() : 'Stakeholder';
		const description = match ? match[2].trim() : '';
		return {
			name,
			description,
		};
	});
	return {
		intro,
		outro,
		stakeholders,
		theme: title,
	};
};

export const parseNumberedList = (listString: string): Array<string> => {
	// parse answer to extract lines beginning with a number and period and space
	const regex = /\d\.\s(.+)/g;
	const lines = listString.matchAll(regex);

	if (lines) {
		return Array.of(...lines).map((matchArray) =>
			stripWhitespaceOrQuotes(matchArray && matchArray[1])
		);
	}

	// Fallback to returning the whole answer if regex did not match
	return [listString];
};

export const stripWhitespaceOrQuotes = (text: string): string => {
	return text.replace(/^["\s]*/g, '').replace(/["\s]*$/g, '');
};

const findFirstMatch = (regex: RegExp, text: string): string | null => {
	const match = text.match(regex);
	return match && match[1];
};

export const parseTargetAudiences = (
	audiencesString: string
): Array<SelectableItem<TargetAudience>> => {
	try {
		const parsed = JSON.parse(audiencesString) as unknown;
		const candidates = Array.isArray(parsed)
			? parsed
			: parsed && typeof parsed === 'object' && Array.isArray((parsed as { audiences?: unknown }).audiences)
				? (parsed as { audiences: unknown[] }).audiences
				: [];
		const structuredAudiences = candidates.flatMap((candidate): Array<SelectableItem<TargetAudience>> => {
			if (!candidate || typeof candidate !== 'object') return [];
			const value = candidate as Partial<Record<keyof TargetAudience, unknown>>;
			const fields: Array<keyof TargetAudience> = [
				'name',
				'profession',
				'age',
				'needs',
				'motivations',
			];
			if (!fields.every((field) => typeof value[field] === 'string')) return [];
			const audience = Object.fromEntries(
				fields.map((field) => [field, (value[field] as string).replace(/\s+/g, ' ').trim()])
			) as TargetAudience;
			return fields.every((field) => audience[field])
				? [{ value: audience, selected: false }]
				: [];
		});
		if (structuredAudiences.length > 0) return structuredAudiences;
	} catch {
		// Historical audience results are plain text and remain supported below.
	}

	const targetAudiences: Array<SelectableItem<TargetAudience>> = [];
	const matches = `${audiencesString}\n\n`.matchAll(
		new RegExp(`^(?:(?:Target )?Audience )?[0-9][.:] ?([\\w- ]+(\n\n)?(.|\n)+?)\n\n`, 'gm')
	);

	for (const match of matches) {
		const audience = match[1];
		if (audience) {
			let name =
				findFirstMatch(
					new RegExp(`(?:Target )?Audience ?[0-9]?[.:] \\*?\\*?"?([\\w- ]+)"?\\*?\\*\\s*?\n`, 'i'),
					audience
				) ||
				findFirstMatch(new RegExp(`\\*?\\*?"?([\\w- ]+)"?:\\*?\\*?\\s*\n`, 'i'), audience) ||
				findFirstMatch(new RegExp(`\\*?\\*?"?([\\w- ]+)"?\\*?\\*?\n`, 'i'), audience) ||
				// If we cannot find a name, we will use an empty string (this will be checked later and found to be invalid)
				'';

			if (name.includes('Target Audience')) {
				// If the name is just "Target Audience" this is not a valid name so we set it to an empty string (which will be checked later and found to be invalid)
				name = '';
			}

			targetAudiences.push({
				value: {
					name: name.trim(),
					motivations: extractFromSingleLine(audience, 'Motivations'),
					needs: extractFromSingleLine(audience, 'Needs'),
					profession: extractFromSingleLine(audience, 'Profession'),
					age:
						extractFromSingleLine(audience, 'Age') || extractFromSingleLine(audience, 'Age Range'),
				},
				selected: false,
			});
		}
	}
	return targetAudiences;
};

export const targetAudiencesAreValid = (
	targetAudiences: Array<SelectableItem<TargetAudience>>
): boolean => {
	return (
		targetAudiences.filter(
			(audience) =>
				audience.value.name &&
				audience.value.profession &&
				audience.value.age &&
				audience.value.needs &&
				audience.value.motivations
		).length > 0
	);
};

export const userPersonaIsValid = (persona: UserPersona): boolean => {
	return Boolean(persona.biography && persona.goals && persona.influences && persona.motivations);
};

export const motivationsIsValid = (motivations: Motivations): boolean => {
	return (
		motivations.stakeholders.filter((stakeholder) => stakeholder.name && stakeholder.description)
			.length > 0
	);
};

export const brainstormingIsValid = (brainstorming: Array<string>): boolean => {
	if (brainstorming.length === 0) {
		return false;
	}

	return !brainstorming.some((item) => item.trim().endsWith(':'));
};
